/* eslint-disable @typescript-eslint/no-misused-promises, no-void */
import { ErrorCode } from "@propelauth/frontend-apis";
import { MfaStatus, useAuthFrontendApis, useMfa } from "@propelauth/frontend-apis-react";
import { useAuthInfo, useLogoutFunction } from "@propelauth/react";
import { Camera, Copy, Download, Loader2, Lock, Monitor, Pencil } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../../../components/ui/input-otp";
import { Label } from "../../../../components/ui/label";
import { uploadCurrentUserAvatar } from "../../../../services/usersEndpoint";
import useHeaderStore from "../../../../store/layout/headerStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";

const MIN_PASSWORD_LEN = 8;

type AccountSettingsPageProps = {
  embedded?: boolean;
};

type ApiError = {
  error_code?: string;
  user_facing_errors?: {
    first_name?: string;
    last_name?: string;
    new_email?: string;
    password?: string;
  };
  field_errors?: {
    code?: string;
  };
};

const SettingRow = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-8 py-3.5">
    <div className="min-w-0">
      <p className="text-[13px] text-foreground">{label}</p>
      {description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p> : null}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const splitFullName = (fullName: string) => {
  const cleaned = fullName.trim();
  if (!cleaned) return { firstName: "", lastName: "" };

  const [firstName, ...rest] = cleaned.split(/\s+/);
  return {
    firstName,
    lastName: rest.join(" "),
  };
};

const toQrImageSrc = (qrValue?: string) => {
  if (!qrValue) return null;
  const value = qrValue.trim();
  if (!value) return null;
  if (value.startsWith("data:image/")) return value;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `data:image/png;base64,${value}`;
};

const AccountSettingsPage: React.FC<AccountSettingsPageProps> = ({ embedded = false }) => {
  const { workspace } = useWorkspaceStore();
  const { setHeaderContent, resetHeader } = useHeaderStore();
  const { user, refreshAuthInfo } = useAuthInfo();
  const authApis = useAuthFrontendApis();
  const { status: mfaStatus, data: mfaData, enableMfa, reloadMfaStatus } = useMfa();
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [fullNameInput, setFullNameInput] = useState("");
  const [emailAddress, setEmailAddress] = useState("");

  const [editingName, setEditingName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [emailPasswordModalOpen, setEmailPasswordModalOpen] = useState(false);
  const [emailPassword, setEmailPassword] = useState("");
  const [emailPasswordError, setEmailPasswordError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaDisableConfirmOpen, setMfaDisableConfirmOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [justEnabledMfa, setJustEnabledMfa] = useState(false);

  const logout = useLogoutFunction();

  useEffect(() => {
    if (embedded) return undefined;
    resetHeader();
    setHeaderContent({
      title: "My Profile",
      description: "Manage your personal information and preferences",
    });

    return () => resetHeader();
  }, [embedded, resetHeader, setHeaderContent]);

  useEffect(() => {
    const userFirstName = ((user as any)?.firstName as string | undefined) ?? "";
    const userLastName = ((user as any)?.lastName as string | undefined) ?? "";
    const userEmail = ((user as any)?.email as string | undefined) ?? "";

    setFirstName(userFirstName);
    setLastName(userLastName);
    setEmailAddress(userEmail);
    setFullNameInput(`${userFirstName} ${userLastName}`.trim());
  }, [user]);

  const isMfaEnabled = mfaStatus === MfaStatus.Enabled;
  const isMfaBusy = mfaStatus === MfaStatus.Loading || mfaStatus === MfaStatus.UnexpectedError;

  const mfaSetupData = useMemo(() => {
    if (mfaStatus !== MfaStatus.Disabled || !mfaData || typeof mfaData !== "object") return null;
    if (!("new_qr" in mfaData)) return null;
    return mfaData as { new_qr?: string; new_secret?: string };
  }, [mfaData, mfaStatus]);

  const mfaQrSrc = useMemo(
    () => toQrImageSrc(mfaSetupData?.new_qr),
    [mfaSetupData?.new_qr],
  );

  const initials = useMemo(() => {
    const first = firstName.trim()?.[0] ?? "";
    const last = lastName.trim()?.[0] ?? "";
    const fromName = `${first}${last}`.toUpperCase();
    if (fromName) return fromName;

    const fallback = emailAddress.trim()?.[0]?.toUpperCase();
    return fallback ?? "";
  }, [emailAddress, firstName, lastName]);

  const avatarSrc = ((user as any)?.pictureUrl as string | undefined) ?? undefined;
  const displayName = `${firstName} ${lastName}`.trim() || "—";

  const resetErrors = () => {
    setNameError(null);
    setEmailError(null);
    setPasswordError(null);
    setMfaError(null);
    setEmailPasswordError(null);
  };

  const handleAvatarEdit = () => {
    avatarFileInputRef.current?.click();
  };

  const handleAvatarFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";
    if (!selectedFile) return;

    const allowedTypes = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);
    if (!allowedTypes.has(selectedFile.type)) {
      notify({
        type: "failure",
        heading: "Unsupported file type",
        messageList: ["Use PNG, JPG, GIF, or WEBP images only."],
      });
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      notify({
        type: "failure",
        heading: "Image too large",
        messageList: ["Avatar must be 2 MB or smaller."],
      });
      return;
    }

    if (!workspace?.id) {
      notify({ type: "failure", heading: "Error", messageList: ["Workspace not found."] });
      return;
    }

    setLoading(true);
    try {
      await uploadCurrentUserAvatar(selectedFile, workspace.id);
      await refreshAuthInfo();
      notify({ type: "success", messageList: ["Profile photo updated."] });
    } catch (error: any) {
      const message =
        typeof error?.response?.data?.detail === "string"
          ? error.response.data.detail
          : "Please try again later.";

      notify({
        type: "failure",
        heading: "Unable to update profile photo",
        messageList: [message],
      });
    } finally {
      setLoading(false);
    }
  };

  const requestEmailChange = async ({
    newEmail,
    password,
  }: {
    newEmail: string;
    password?: string;
  }): Promise<"ok" | "password_required" | "error"> => {
    const response = await authApis.updateEmail({
      new_email: newEmail,
      password,
    });

    if (response.ok) {
      return "ok";
    }

    const error = response.error as ApiError;
    if (error?.error_code === ErrorCode.IncorrectPassword) {
      return "password_required";
    }

    if (error?.user_facing_errors?.new_email) {
      setEmailError(error.user_facing_errors.new_email);
      return "error";
    }

    notify({
      type: "failure",
      heading: "Unable to update email",
      messageList: ["Please try again later."],
    });
    return "error";
  };

  const handleSaveName = async () => {
    if (!user) return;
    resetErrors();

    const { firstName: nextFirstName, lastName: nextLastName } = splitFullName(fullNameInput);
    const currentFirst = ((user as any)?.firstName as string | undefined) ?? "";
    const currentLast = ((user as any)?.lastName as string | undefined) ?? "";

    if (nextFirstName === currentFirst && nextLastName === currentLast) {
      setEditingName(false);
      return;
    }

    setLoading(true);
    try {
      const userProperties = ((user as any)?.properties ?? {}) as Record<string, unknown>;
      const response = await authApis.updateUserMetadata({
        first_name: nextFirstName || undefined,
        last_name: nextLastName || undefined,
        properties: userProperties,
      });

      if (!response.ok) {
        const error = response.error as ApiError;
        if (error?.user_facing_errors?.first_name) {
          setNameError(error.user_facing_errors.first_name);
          return;
        }
        if (error?.user_facing_errors?.last_name) {
          setNameError(error.user_facing_errors.last_name);
          return;
        }
        notify({
          type: "failure",
          heading: "Unable to update profile",
          messageList: ["Please try again later."],
        });
        return;
      }

      setFirstName(nextFirstName);
      setLastName(nextLastName);
      setEditingName(false);
      notify({ type: "success", messageList: ["Name updated."] });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!user) return;
    resetErrors();

    const nextEmail = emailAddress.trim();
    const currentEmail = ((user as any)?.email as string | undefined) ?? "";

    if (!nextEmail) {
      setEmailError("Email is required");
      return;
    }

    if (nextEmail === currentEmail) {
      setEditingEmail(false);
      return;
    }

    setLoading(true);
    try {
      const status = await requestEmailChange({ newEmail: nextEmail });
      if (status === "password_required") {
        setPendingEmail(nextEmail);
        setEmailPassword("");
        setEmailPasswordError(null);
        setEmailPasswordModalOpen(true);
        return;
      }
      if (status === "ok") {
        setEditingEmail(false);
        notify({
          type: "success",
          messageList: ["Email update initiated. Please check your email to confirm the change."],
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEmailPassword = async () => {
    resetErrors();
    if (!pendingEmail) return;

    if (!emailPassword.trim()) {
      setEmailPasswordError("Password is required");
      return;
    }

    setLoading(true);
    try {
      const status = await requestEmailChange({ newEmail: pendingEmail, password: emailPassword.trim() });
      if (status === "ok") {
        setEditingEmail(false);
        setEmailPasswordModalOpen(false);
        setEmailPassword("");
        setPendingEmail(null);
        notify({
          type: "success",
          messageList: ["Email update initiated. Please check your email to confirm the change."],
        });
      } else if (status === "password_required") {
        setEmailPasswordError("Incorrect password");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    resetErrors();

    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedCurrent || !trimmedNew || !trimmedConfirm) {
      setPasswordError("All password fields are required");
      return;
    }

    if (trimmedNew.length < MIN_PASSWORD_LEN) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LEN} characters`);
      return;
    }

    if (trimmedNew !== trimmedConfirm) {
      setPasswordError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await authApis.updatePassword({
        password: trimmedNew,
        current_password: trimmedCurrent,
      });

      if (response.ok) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordModalOpen(false);
        notify({ type: "success", messageList: ["Password updated successfully."] });
        return;
      }

      const error = response.error as ApiError;
      if (error?.user_facing_errors?.password) {
        setPasswordError(error.user_facing_errors.password);
        return;
      }

      if (error?.error_code === ErrorCode.IncorrectPassword) {
        setPasswordError("Current password is incorrect");
        return;
      }

      notify({
        type: "failure",
        heading: "Unable to update password",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMfa = async () => {
    resetErrors();

    if (mfaStatus !== MfaStatus.Disabled) return;
    if (mfaCode.trim().length < 6) {
      setMfaError("Enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await enableMfa({ code: mfaCode.trim() });
      if (response.ok) {
        setMfaSetupOpen(false);
        setMfaCode("");
        setJustEnabledMfa(true);
        await reloadMfaStatus();
        notify({ type: "success", messageList: ["Two-factor authentication enabled."] });
        return;
      }

      const error = response.error as ApiError;
      if (error?.field_errors?.code) {
        setMfaError("Invalid code");
        return;
      }

      notify({
        type: "failure",
        heading: "Unable to enable 2FA",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  // Show backup codes after user just enabled MFA
  useEffect(() => {
    if (
      justEnabledMfa &&
      mfaStatus === MfaStatus.Enabled &&
      mfaData &&
      typeof mfaData === "object" &&
      "backup_codes" in mfaData
    ) {
      const codes = (mfaData as { backup_codes: string[] }).backup_codes;
      if (codes?.length) {
        setBackupCodes(codes);
        setBackupCodesOpen(true);
        setJustEnabledMfa(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaStatus, mfaData, justEnabledMfa]);

  const handleDisableMfa = async () => {
    resetErrors();
    if (mfaStatus !== MfaStatus.Enabled) return;

    setLoading(true);
    try {
      // Direct fetch to work around SDK sending POST with no body (causes invalid_json error)
      const authUrl = import.meta.env.VITE_AUTH_URL ?? "";
      const res = await fetch(`${authUrl}/api/fe/v3/mfa_disable`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": "-.-",
        },
        body: JSON.stringify({}),
      });

      if (res.ok) {
        setMfaDisableConfirmOpen(false);
        setBackupCodes([]);
        await reloadMfaStatus();
        notify({ type: "success", messageList: ["Two-factor authentication disabled."] });
        return;
      }

      notify({
        type: "failure",
        heading: "Unable to disable 2FA",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  const handleSignOutAllSessions = async () => {
    setLoading(true);
    try {
      await logout(true);
    } catch {
      notify({
        type: "failure",
        heading: "Unable to sign out",
        messageList: ["Please try again later."],
      });
      setSignOutConfirmOpen(false);
      setLoading(false);
    }
  };

  const handleOpenMfaSetup = () => {
    setMfaCode("");
    setMfaError(null);
    setMfaSetupOpen(true);
    reloadMfaStatus().catch(() => {
      notify({
        type: "failure",
        heading: "Unable to refresh 2FA setup",
        messageList: ["Please close and reopen the dialog."],
      });
    });
  };

  return (
    <div className={`relative ${embedded ? "" : "mx-auto max-w-5xl px-6 py-6"}`}>
      {loading ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : null}

      <div className={`space-y-8 ${embedded ? "" : "mt-6"}`}>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[15px] font-semibold text-foreground">My Profile</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Edit your personal information, enable 2FA, or manage your account
            </p>
          </div>
          <div className="relative">
            <Avatar className="h-16 w-16">
              <AvatarImage src={avatarSrc} alt="Profile photo" />
              <AvatarFallback className="bg-muted text-lg font-medium text-muted-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={handleAvatarEdit}
              className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-accent-hover"
            >
              <Camera className="h-3 w-3" />
            </button>
            <input
              ref={avatarFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(event) => {
                handleAvatarFileSelected(event).catch(() => {});
              }}
            />
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Account settings</h2>
          <div className="divide-y divide-border">
            <SettingRow label="Email">
              <div className="flex items-center gap-3">
                {editingEmail ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={emailAddress}
                        onChange={(event) => setEmailAddress(event.target.value)}
                        className="h-7 w-56 text-[13px]"
                        autoFocus
                      />
                      <Button size="sm" className="h-7 text-[12px]" onClick={() => {
                        handleSaveEmail().catch(() => {});
                      }}>
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[12px]"
                        onClick={() => {
                          setEditingEmail(false);
                          setEmailAddress(((user as any)?.email as string | undefined) ?? "");
                          setEmailError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {emailError ? <p className="text-[11px] text-status-cancelled">{emailError}</p> : null}
                  </div>
                ) : (
                  <>
                    <span className="text-[13px] text-foreground">{emailAddress || "—"}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingEmail(true);
                        setEmailError(null);
                      }}
                      className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </SettingRow>

            <SettingRow label="Name">
              <div className="flex items-center gap-3">
                {editingName ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={fullNameInput}
                        onChange={(event) => setFullNameInput(event.target.value)}
                        className="h-7 w-44 text-[13px]"
                        autoFocus
                      />
                      <Button size="sm" className="h-7 text-[12px]" onClick={() => {
                        handleSaveName().catch(() => {});
                      }}>
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[12px]"
                        onClick={() => {
                          setEditingName(false);
                          setFullNameInput(displayName === "—" ? "" : displayName);
                          setNameError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {nameError ? <p className="text-[11px] text-status-cancelled">{nameError}</p> : null}
                  </div>
                ) : (
                  <>
                    <span className="text-[13px] text-foreground">{displayName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingName(true);
                        setNameError(null);
                      }}
                      className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </SettingRow>
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Security settings</h2>
          <div className="divide-y divide-border">
            <SettingRow label="Password">
              <Button size="sm" className="h-8 gap-1.5 text-[12px]" onClick={() => {
                setPasswordModalOpen(true);
                setPasswordError(null);
              }}>
                <Pencil className="h-3 w-3" />
                Change password
              </Button>
            </SettingRow>

            <SettingRow
              label="Two-factor authentication"
              description={isMfaEnabled ? "2FA is enabled on your account" : "Add an extra layer of security"}
            >
              {isMfaEnabled ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 border-status-cancelled/30 text-[12px] text-status-cancelled hover:bg-status-cancelled/10"
                  onClick={() => setMfaDisableConfirmOpen(true)}
                  disabled={isMfaBusy}
                >
                  <Lock className="h-3 w-3" />
                  Disable 2FA
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-8 gap-1.5 text-[12px]"
                  onClick={handleOpenMfaSetup}
                  disabled={isMfaBusy}
                >
                  <Lock className="h-3 w-3" />
                  Enable 2FA
                </Button>
              )}
            </SettingRow>
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Active sessions</h2>
          <p className="mb-3 text-[11px] text-muted-foreground">Manage your active sessions across devices</p>
          <div className="divide-y divide-border rounded-lg border border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <Monitor className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-[13px] text-foreground">Current browser session</p>
                  <p className="text-[11px] text-muted-foreground">Current session · Last active now</p>
                </div>
              </div>
              <span className="text-[11px] font-medium text-status-active">Active</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 h-7 text-[12px]"
            onClick={() => setSignOutConfirmOpen(true)}
          >
            Sign out all sessions
          </Button>
        </div>
      </div>

      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Update your password to keep your account secure.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="current-password" className="mb-1 block text-[12px] font-medium text-foreground">Current password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="new-password" className="mb-1 block text-[12px] font-medium text-foreground">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="mb-1 block text-[12px] font-medium text-foreground">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>

            {passwordError ? <p className="text-[12px] text-status-cancelled">{passwordError}</p> : null}
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => {
              handleUpdatePassword().catch(() => {});
            }}>
              Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailPasswordModalOpen} onOpenChange={setEmailPasswordModalOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Confirm password</DialogTitle>
            <DialogDescription>
              Please enter your current password to change your email address.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-email-password" className="text-[12px] font-medium text-foreground">
              Current password
            </Label>
            <Input
              id="confirm-email-password"
              type="password"
              value={emailPassword}
              onChange={(event) => setEmailPassword(event.target.value)}
            />
            {emailPasswordError ? <p className="text-[11px] text-status-cancelled">{emailPasswordError}</p> : null}
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setEmailPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => {
              handleConfirmEmailPassword().catch(() => {});
            }} disabled={loading}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mfaSetupOpen} onOpenChange={setMfaSetupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">
              Set up 2FA via Authenticator App
            </DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground">
              Scan the QR code and enter the 6-digit code from your authenticator app.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-2">
            {mfaQrSrc ? (
              <img src={mfaQrSrc} alt="MFA QR Code" className="h-48 w-48 rounded-lg border border-border bg-white p-2" />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center rounded-lg border border-border bg-muted">
                <div className="text-center">
                  <Lock className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                  <span className="text-[12px] text-muted-foreground">QR Code</span>
                </div>
              </div>
            )}
          </div>

          {mfaSetupData?.new_secret ? (
            <p className="text-center text-[11px] text-muted-foreground">
              Manual code: <span className="font-mono text-foreground">{mfaSetupData.new_secret}</span>
            </p>
          ) : null}

          <div className="space-y-2 pt-2">
            <p className="text-[13px] font-medium text-foreground">2FA code</p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {mfaError ? <p className="text-center text-[12px] text-status-cancelled">{mfaError}</p> : null}
          </div>

          <div className="flex gap-3 pt-3">
            <Button
              variant="outline"
              className="h-8 flex-1 text-[12px]"
              onClick={() => {
                setMfaSetupOpen(false);
                setMfaCode("");
                setMfaError(null);
              }}
            >
              Back
            </Button>
            <Button
              className="h-8 flex-1 text-[12px]"
              disabled={mfaCode.trim().length < 6 || isMfaBusy}
              onClick={() => {
                handleEnableMfa().catch(() => {});
              }}
            >
              Enable MFA
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mfaDisableConfirmOpen} onOpenChange={setMfaDisableConfirmOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Disabling 2FA will reduce the security of your account. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setMfaDisableConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                handleDisableMfa().catch(() => {});
              }}
              disabled={isMfaBusy}
            >
              Disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={backupCodesOpen} onOpenChange={(open) => {
        if (!open) {
          setBackupCodesOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">Backup Codes</DialogTitle>
            <DialogDescription className="text-[12px]">
              Save these backup codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/50 p-4">
            {backupCodes.map((code, i) => (
              <span key={i} className="font-mono text-[13px] text-foreground">{code}</span>
            ))}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 gap-1.5 text-[12px]"
              onClick={() => {
                const text = backupCodes.join("\n");
                navigator.clipboard.writeText(text).then(() => {
                  notify({ type: "success", messageList: ["Backup codes copied to clipboard."] });
                }).catch(() => {});
              }}
            >
              <Copy className="h-3 w-3" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 gap-1.5 text-[12px]"
              onClick={() => {
                const text = backupCodes.join("\n");
                const blob = new Blob([text], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "hyperengage-2fa-backup-codes.txt";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="h-3 w-3" />
              Download
            </Button>
          </div>

          <DialogFooter>
            <Button size="sm" onClick={() => setBackupCodesOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signOutConfirmOpen} onOpenChange={setSignOutConfirmOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Sign Out All Sessions</DialogTitle>
            <DialogDescription>
              This will sign you out of all sessions including this one. You will need to log in again.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setSignOutConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                handleSignOutAllSessions().catch(() => {});
              }}
            >
              Sign out all sessions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountSettingsPage;
